/* eslint-disable */
import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import { PageHeader } from "../../components/shared/page-header";
import { customBaseUrl, httpGet } from "../../store/http";
import { hideLoader, showLoader } from "../../utils/loader";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { NotificationManager } from "react-notifications";
// import RecentUsersComponent from './RecentUsersComponent';
// import TransactionSummaryComponent from './TransactionSummaryComponent';
// import LogSummaryComponent from './LogSummaryComponent';
import { format } from "date-fns";
import {
  Box,
  Stack,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Grid,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { SectionHeader } from "./shared/components/section-header";
import { BoxListTile } from "./shared/components/box-list";
import { EMPTY_USER_STATS } from "./shared/empty-states/user";
import { EMPTY_TRANSACTIONS } from "./shared/empty-states/transactions";
import { EMPTY_FILTERS } from "./shared/empty-states/filters";
import { SectionCard } from "./shared/components/section-card";
import { formatNaira, formatDateTime } from "./shared/utils";
import RefreshCountdown from "./shared/components/show-about-to-countdown";

// LOCALIZED CONSTANTS
const WAYA_PRODUCTS = [
  "card",
  "pos",
  "bills payment",
  "loan disbursement",
  "loan repayment",
  "intra-bank transfer",
  "inter-bank transfer",
];
const WAYA_USER_TYPES = ["personal", "Non-CAC", "Corporate"];
const REFRESH_INTERVAL = 30;

const Index = () => {
  const [transaction, setTransactions] = useState(EMPTY_TRANSACTIONS);
  const [tierData, setTierData] = useState(EMPTY_USER_STATS);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [secondsLeft, setSecondsLeft] = useState(REFRESH_INTERVAL);

  const getStat = async () => {
    showLoader();
    try {
      const systemUrl = `${customBaseUrl.tranv2}/api/v1/admin/statistics/system?startDate=${formatDateTime(filters.dateRange.from)}&endDate=${formatDateTime(filters.dateRange.to)}`;

      // const tierUrl = `${customBaseUrl.tranv2}/api/v1/admin/statistics/tier-accounts/cumulative?startDate=${filters.dateRange.from}&endDate=${filters.dateRange.to}`;
      const userUrl = `${customBaseUrl.authUrl}/api/v1/admin/analytics/dashboard`;

      const [systemRes, tierRes] = await Promise.all([
        httpGet(systemUrl),
        httpGet(userUrl),
      ]);

      if (systemRes.data) {
        setTransactions(systemRes.data);
      }
      if (tierRes.data) {
        setTierData(tierRes.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard statistics:", error);
    } finally {
      hideLoader();
    }
  };

  const exportAnalyticsToExcel = () => {
    if (!transaction && !tierData) {
      NotificationManager.warning("No analytics data available to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const data = [];

    // System Overview
    if (transaction) {
      data.push(["SYSTEM TRANSACTION OVERVIEW"]);
      data.push(["Metric", "Value"]);
      data.push(["Total Transactions", transaction.totalTransactions]);
      data.push([
        "Successful Transactions",
        transaction.successfulTransactions,
      ]);
      data.push(["Pending Transactions", transaction.pendingTransactions]);
      data.push(["Failed Transactions", transaction.failedTransactions]);
      data.push([]);
    }

    // Tier Analytics
    if (tierData) {
      const categories = [
        { key: "personal", name: "PERSONAL ACCOUNTS" },
        { key: "nonCac", name: "NON-CAC BUSINESS ACCOUNTS" },
        { key: "cac", name: "CAC BUSINESS ACCOUNTS" },
      ];

      categories.forEach((cat) => {
        const stats = tierData[cat.key];
        if (stats) {
          data.push([cat.name]);
          data.push(["Tier", "Count", "Amount (₦)"]);
          data.push(["Tier 1", stats.tier1, stats.tier1Amount]);
          data.push(["Tier 2", stats.tier2, stats.tier2Amount]);
          data.push(["Tier 3", stats.tier3, stats.tier3Amount]);
          data.push(["Tier 4", stats.tier4, stats.tier4Amount]);
          data.push(["Total", stats.total, stats.totalAmount]);
          data.push([]);
        }
      });

      data.push(["OVERALL CUMULATIVE SUMMARY"]);
      data.push(["Metric", "Value"]);
      data.push(["Total Accounts", tierData.total]);
      data.push(["Total Amount (₦)", tierData.totalAmount]);
    }

    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Analytics");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    let filename = `Analytics_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`;
    if (filters.dateRange.from && filters.dateRange.to) {
      filename = `Analytics_${filters.dateRange.from}_to_${filters.dateRange.to}.xlsx`;
    }

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  useEffect(() => {
    if (filters.dateRange.to && filters.dateRange.from) {
      getStat(filters.dateRange.from, filters.dateRange.to);
    }
  }, [filters.dateRange, filters.product]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === 1) {
          getStat(EMPTY_FILTERS.dateRange.from, EMPTY_FILTERS.dateRange.to);
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft]);

  const resetSecondsLeft = () => {
    setSecondsLeft(REFRESH_INTERVAL);
  };

  const handleProductChange = (value) => {
    setFilters({ ...filters, product: value });
  };

  const handleUserTypeChange = (value) => {
    setFilters({ ...filters, userType: value });
  };

  const handleDateRangeChange = (range) => {
    const now = new Date();
    let from = new Date();

    switch (range) {
      case "24h":
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "7d":
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30d":
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "90d":
        from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
    }

    setFilters({ ...filters, dateRange: { from, to: now } });
  };

  const resetFilters = () => {
    const now = new Date();
    const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    setFilters({
      dateRange: { from, to: now },
      product: "all",
      userType: "all",
    });
  };

  const formatDateRange = () => {
    const from = format(filters.dateRange.from, "MMM d");
    const to = format(filters.dateRange.to, "MMM d, yyyy");
    return `${from} - ${to}`;
  };
  const activeFilters = [];

  if (filters.product !== "all") {
    activeFilters.push({
      label: `Product: ${filters.product}`,
      key: "product",
    });
  }

  if (filters.userType !== "all") {
    activeFilters.push({
      label: `User: ${filters.userType}`,
      key: "userType",
    });
  }

  return (
    <Layout>
      <div>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <PageHeader
            title="Dashboard"
            description="Monitor platform activity and key metrics"
          />
          <RefreshCountdown seconds={secondsLeft} reset={resetSecondsLeft} />
        </Box>
        <Stack spacing={2} marginBottom={4} marginTop={4}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ lg: "center" }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon fontSize="small" />

                <Select
                  size="small"
                  defaultValue="7d"
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="24h">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                </Select>

                <Typography variant="caption" color="text.secondary">
                  {formatDateRange()}
                </Typography>
              </Stack>

              <Select
                size="small"
                value={filters.product}
                onChange={(e) => handleProductChange(e.target.value)}
                sx={{ width: 160 }}
              >
                <MenuItem value="all">All Products</MenuItem>
                {WAYA_PRODUCTS.map((product) => (
                  <MenuItem key={product} value={product}>
                    {product.charAt(0).toUpperCase() + product.slice(1)}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={filters.userType}
                onChange={(e) => {
                  handleUserTypeChange(e.target.value);
                  resetSecondsLeft();
                }}
                sx={{ width: 160 }}
              >
                <MenuItem value="all">All Users</MenuItem>
                {WAYA_USER_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Button variant="outlined" onClick={resetFilters}>
              Reset Filters
            </Button>
          </Stack>

          {activeFilters.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeFilters.map((filter) => (
                  <Chip key={filter.key} label={filter.label} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
        <Box>
          <SectionHeader title="Transactions" />
          <Grid container spacing={2}>
            <SectionCard
              caption="Total Transactions"
              value={transaction.totalTransactions}
            />
            <SectionCard
              caption="Successful"
              value={transaction.successfulTransactions}
            />
            <SectionCard
              caption="Failed"
              value={transaction.failedTransactions}
            />
          </Grid>
          <Box marginTop={2}>
            <Typography variant="subtitle1" marginBottom={1} fontWeight={400}>
              Transaction Details
            </Typography>
            <Stack spacing={1}>
              <BoxListTile
                status={"pending"}
                primary={`Pending Transactions`}
                secondary={transaction.pendingTransactions}
              />
              <BoxListTile
                status={"processing"}
                primary={`Pending Amount`}
                secondary={formatNaira(transaction.totalPendingAmount)}
              />
              <BoxListTile
                status={"completed"}
                primary={`Total Successful Amount`}
                secondary={formatNaira(transaction.totalSuccessfulAmount)}
              />
              <BoxListTile
                status={"refunded"}
                primary={`Last Updated`}
                secondary={new Date(transaction.lastUpdated).toLocaleString()}
              />
            </Stack>
          </Box>
        </Box>

        <Box marginTop={4}>
          <SectionHeader title="Users" />
          <Grid container spacing={2}>
            <SectionCard
              caption="Total Users"
              value={tierData.quickStats.totalUsers}
            />
            <SectionCard
              caption="New Signups (30d)"
              value={tierData.quickStats.newUsersThisMonth}
            />
          </Grid>
          <Box marginTop={2}>
            <Typography variant="subtitle1" marginBottom={1} fontWeight={400}>
              Users Details
            </Typography>
            <Stack spacing={1}>
              <BoxListTile
                status={"pending"}
                primary={`Pending Verifications`}
                secondary={tierData.quickStats.pendingVerifications}
              />
            </Stack>
          </Box>
        </Box>
        {/* <Tabs
          defaultActiveKey="1"
          size="large"
          tabBarStyle={{ paddingLeft: 20 }}
          onChange={handleChange}
          animated
          tabBarExtraContent={
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Button
                type="primary"
                onClick={exportAnalyticsToExcel}
                disabled={!transaction && !tierData}
              >
                Download Analytics
              </Button>
              <SwitchToggler handleToggler={handleToggler} state={state} />
            </div>
          }
        >
          <TabPane tab={<></>} key="1">
            <OverviewComponent
              state={state}
              transaction={transaction}
              tierData={tierData}
            />
          </TabPane>
        </Tabs> */}
      </div>
    </Layout>
  );
};

export default Index;
